import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountsService } from '../services/accountsService';
import { ArrowLeft, ShieldCheck, Phone, KeyRound } from 'lucide-react';

/** ForgotPassword: self-service reset for staff or customers via a mock phone OTP, on a public route. */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'verify' | 'done'
  const [type, setType] = useState('user');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [mockOtp, setMockOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const requestOtp = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await accountsService.requestOtp(type, phone.trim());
      setMockOtp(res.mock_otp || '');
      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    setBusy(true);
    setError('');
    try {
      await accountsService.resetPassword(type, phone.trim(), code.trim(), newPassword);
      setStep('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </button>

        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-6 w-6 text-amber-600" />
          <h1 className="text-2xl font-bold text-gray-900">Reset password</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          We'll send a one-time code to your phone to verify it's you.
        </p>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {step === 'request' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Account type</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'user', label: 'Staff user' },
                  { key: 'customer', label: 'Customer' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setType(opt.key)}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      type === opt.key
                        ? 'border-amber-500 bg-amber-50 text-amber-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Registered phone number"
                  className="w-full pl-10 pr-3 py-2.5 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                />
              </div>
            </div>
            <button
              onClick={requestOtp}
              disabled={busy || !phone.trim()}
              className="w-full rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium py-2.5 transition-colors"
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </div>
        )}

        {step === 'verify' && (
          <div className="space-y-4">
            {mockOtp && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <strong>Mock OTP (dev):</strong> {mockOtp}
                <div className="text-xs text-amber-700/80 mt-0.5">
                  In production this is sent by SMS, not shown here.
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Verification code</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code"
                inputMode="numeric"
                className="w-full px-3 py-2.5 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">New password (min 6 chars)</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                />
              </div>
            </div>
            <button
              onClick={resetPassword}
              disabled={busy || !code.trim() || newPassword.length < 6}
              className="w-full rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium py-2.5 transition-colors"
            >
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
            <button
              onClick={() => { setStep('request'); setCode(''); setMockOtp(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-800"
            >
              Use a different phone
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-4">
            <ShieldCheck className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-gray-800 font-medium">Password reset successfully.</p>
            <p className="text-sm text-gray-500 mt-1 mb-5">You can now sign in with your new password.</p>
            <button
              onClick={() => navigate('/')}
              className="rounded-md bg-amber-600 hover:bg-amber-700 text-white font-medium px-6 py-2.5 transition-colors"
            >
              Go to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
